// apps/patient/src/components/programs/LocationSelectionModal.tsx

import { useState, useEffect } from 'react';
import { MapPin, Plus, ChevronRight, AlertTriangle, Check, X, RefreshCw } from 'lucide-react';
import { PatientLocation, ExerciseSubstitution, ExerciseEquipmentCheck } from '@prism-ehr/shared';
import { LOCATION_TYPE_ICONS, LOCATION_TYPE_LABELS } from '../../constants/equipment';

interface LocationSelectionModalProps {
  locations: PatientLocation[];
  equipmentGaps: ExerciseEquipmentCheck[];
  proposedSubstitutions: Map<string, ExerciseSubstitution[]>;
  selectedLocation: PatientLocation | null;
  onSelectLocation: (location: PatientLocation) => void;
  onConfirm: (location: PatientLocation, acceptedSubstitutions: ExerciseSubstitution[]) => void;
  onCreateLocation: () => void;
  onCancel: () => void;
  loading?: boolean;
}

export function LocationSelectionModal({
  locations,
  equipmentGaps,
  proposedSubstitutions,
  selectedLocation,
  onSelectLocation,
  onConfirm,
  onCreateLocation,
  onCancel,
  loading = false
}: LocationSelectionModalProps) {
  const [acceptedSubstitutions, setAcceptedSubstitutions] = useState<ExerciseSubstitution[]>([]);
  const [showSubstitutionReview, setShowSubstitutionReview] = useState(false);

  // When equipment gaps change, auto-accept first substitution for each
  useEffect(() => {
    if (equipmentGaps.length > 0 && proposedSubstitutions.size > 0) {
      const autoAccepted: ExerciseSubstitution[] = [];
      equipmentGaps.forEach((gap) => {
        const subs = proposedSubstitutions.get(gap.exercise_id);
        if (subs && subs.length > 0) {
          autoAccepted.push(subs[0]);
        }
      });
      setAcceptedSubstitutions(autoAccepted);
    } else {
      setAcceptedSubstitutions([]);
    }
  }, [equipmentGaps, proposedSubstitutions]);

  const handleConfirm = () => {
    if (selectedLocation) {
      onConfirm(selectedLocation, acceptedSubstitutions);
    }
  };

  const toggleSubstitution = (exerciseId: string, substitution: ExerciseSubstitution) => {
    setAcceptedSubstitutions((prev) => {
      const existing = prev.find((s) => s.original_exercise_id === exerciseId);
      if (existing?.substitute_exercise_id === substitution.substitute_exercise_id) {
        // Remove this substitution
        return prev.filter((s) => s.original_exercise_id !== exerciseId);
      }
      // Replace with new substitution
      return [
        ...prev.filter((s) => s.original_exercise_id !== exerciseId),
        substitution
      ];
    });
  };

  const defaultLocation = locations.find((l) => l.is_default);
  const sortedLocations = [...locations].sort((a, b) => {
    if (a.is_default) return -1;
    if (b.is_default) return 1;
    if (a.last_used_at && b.last_used_at) {
      return new Date(b.last_used_at).getTime() - new Date(a.last_used_at).getTime();
    }
    return 0;
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Where are you working out?</h2>
          <p className="text-sm text-gray-500 mt-1">
            Select your location to optimize exercises for available equipment
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-8 w-8 text-primary animate-spin" />
            </div>
          ) : (
            <>
              {/* Location Selection */}
              {!showSubstitutionReview && (
                <div className="space-y-2">
                  {sortedLocations.map((location) => (
                    <button
                      key={location.id}
                      onClick={() => onSelectLocation(location)}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                        selectedLocation?.id === location.id
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">
                            {LOCATION_TYPE_ICONS[location.location_type] || '📍'}
                          </span>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{location.name}</span>
                              {location.is_default && (
                                <span className="px-1.5 py-0.5 text-xs bg-primary/10 text-primary rounded">
                                  Default
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-gray-500">
                              {location.equipment.length} equipment items
                            </span>
                          </div>
                        </div>
                        {selectedLocation?.id === location.id && (
                          <Check className="h-5 w-5 text-primary" />
                        )}
                      </div>
                    </button>
                  ))}

                  {/* Add Location Option */}
                  <button
                    onClick={onCreateLocation}
                    className="w-full p-4 rounded-lg border-2 border-dashed border-gray-300 text-left hover:border-gray-400 transition-colors"
                  >
                    <div className="flex items-center gap-3 text-gray-600">
                      <Plus className="h-5 w-5" />
                      <span>Add New Location</span>
                    </div>
                  </button>

                  {/* Equipment Gaps Warning */}
                  {selectedLocation && equipmentGaps.length > 0 && (
                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800">
                            {equipmentGaps.length} exercise{equipmentGaps.length > 1 ? 's' : ''} need
                            equipment not at this location
                          </p>
                          <p className="text-sm text-amber-700 mt-1">
                            We've found substitutes for {acceptedSubstitutions.length} of them.
                          </p>
                          <button
                            onClick={() => setShowSubstitutionReview(true)}
                            className="text-sm text-amber-800 font-medium mt-2 hover:underline"
                          >
                            Review Substitutions →
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Substitution Review */}
              {showSubstitutionReview && (
                <div className="space-y-4">
                  <button
                    onClick={() => setShowSubstitutionReview(false)}
                    className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
                  >
                    ← Back to locations
                  </button>

                  <h3 className="font-medium">Exercise Substitutions</h3>
                  <p className="text-sm text-gray-500">
                    Select which substitutes to use for exercises requiring unavailable equipment.
                  </p>

                  <div className="space-y-3">
                    {equipmentGaps.map((gap) => {
                      const subs = proposedSubstitutions.get(gap.exercise_id) || [];
                      const acceptedSub = acceptedSubstitutions.find(
                        (s) => s.original_exercise_id === gap.exercise_id
                      );

                      return (
                        <div key={gap.exercise_id} className="border rounded-lg p-4">
                          <div className="mb-3">
                            <p className="font-medium text-gray-900">{gap.exercise_name}</p>
                            <p className="text-sm text-red-600">
                              Missing: {gap.equipment_missing.join(', ')}
                            </p>
                          </div>

                          {subs.length > 0 ? (
                            <div className="space-y-2">
                              <p className="text-xs text-gray-500 uppercase font-medium">
                                Available substitutes:
                              </p>
                              {subs.map((sub) => (
                                <button
                                  key={sub.substitute_exercise_id}
                                  onClick={() => toggleSubstitution(gap.exercise_id, sub)}
                                  className={`w-full p-3 rounded-lg border text-left transition-colors ${
                                    acceptedSub?.substitute_exercise_id === sub.substitute_exercise_id
                                      ? 'border-primary bg-primary/5'
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-medium text-sm">
                                        {sub.substitute_exercise_name}
                                      </p>
                                      {sub.match_score && (
                                        <p className="text-xs text-gray-500">
                                          {sub.match_score}% match
                                        </p>
                                      )}
                                    </div>
                                    {acceptedSub?.substitute_exercise_id ===
                                      sub.substitute_exercise_id && (
                                      <Check className="h-4 w-4 text-primary" />
                                    )}
                                  </div>
                                </button>
                              ))}

                              {/* Skip option */}
                              <button
                                onClick={() =>
                                  setAcceptedSubstitutions((prev) =>
                                    prev.filter((s) => s.original_exercise_id !== gap.exercise_id)
                                  )
                                }
                                className={`w-full p-3 rounded-lg border text-left transition-colors ${
                                  !acceptedSub
                                    ? 'border-gray-400 bg-gray-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <span className="text-sm text-gray-600">
                                  Skip this exercise (no substitute)
                                </span>
                              </button>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">
                              No suitable substitutes found. This exercise will be skipped.
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-between">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedLocation || loading}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Start Workout
          </button>
        </div>
      </div>
    </div>
  );
}
